/* eslint-disable @typescript-eslint/no-explicit-any */
import { ImportData } from 'routes/logged-in/imports/ImporterRoute';
import {
  createImporterConfig,
  getImportConfigFromType,
  getSeparatorSymbol,
} from 'utils/importerUtils';
import { safeToNumber } from '@tock/coretypes';
import { stringify } from '@tock/query-string';
import { toNumber } from 'es-toolkit/compat';
import ClowncarAppConstants from 'constants/ClowncarAppConstants';
import protos from 'utils/protos';

const { DataImport } = protos;

export function fetchTicketTypes(businessId: number) {
  return {
    TockApi: {
      getListProto: `/internal/business/${businessId}/ticket/type/list`,
      type: ClowncarAppConstants.IMPORT_FETCH_TICKET_TYPES,
      scope: {
        businessId: safeToNumber(businessId),
      },
    },
  };
}

/**
 * Importer for booking systems with two files - a reservation and guest file.
 * @param {Object} request Import request in json format.
 * @returns {Object} A redux TockApi action.
 */
export function importData(
  guestFile: any,
  resosFile: any,
  guestFileName: string | undefined,
  resoFileName: string | undefined,
  input: ImportData
): ApiAction {
  input.separator = getSeparatorSymbol(input.separator);
  const request = {
    dryRun: input.isDryRun,
    importerConfig: {
      businessId: input.businessId,
      importerType: input.importerType,
      isDryRun: input.isDryRun,
      separator: input.separator,
      smsReminderEnabled: input.smsReminderEnabled,
      skipWalkins: input.skipWalkins,
      allowReimport: input.allowReimport,
      allowRebalancing: input.allowRebalancing,
      providedVipTags: input.providedVipTags === '' ? undefined : input.providedVipTags,
      guestFullNameOrder: input.guestFullNameOrder,
      resoFullNameOrder: input.resoFullNameOrder,
      multipleHeaderDetection: input.multipleHeaderDetection,
      markForDeletion: input.markForDeletion,
      formatVersion: getImportConfigFromType(input.importerType),
      existingTicketTypeId:
        input.existingTicketTypeId === '' ? undefined : input.existingTicketTypeId,
    },
    businessId: Number(input.businessId),
    guestDataTsv: guestFile,
    futureReservationDataTsv: resosFile,
    guestFileName: guestFileName || '',
    resoFileName: resoFileName || '',
    async: true,
  };
  let toastMessage = '';
  if (input.isDryRun) {
    toastMessage = 'Data validation completed.';
  } else {
    toastMessage = 'Data import submitted.';
  }
  return {
    TockApi: {
      postProto: '/data/import/double',
      request: DataImport.fromObject(request),
      type: ClowncarAppConstants.IMPORT_UPLOAD,
      toast: toastMessage,
      scope: {
        businessId: safeToNumber(request.businessId),
      },
    },
  };
}

/**
 * Importer for booking systems with only one file that contains both guest and reservation information.
 * @param {Object} request Import request in json format.
 * @returns {Object} A redux TockApi action.
 */
export function importFileData(
  file: any,
  fileName: string | undefined,
  fileType: number,
  input: ImportData
): ApiAction {
  const {
    formatVersion,
    separator,
    smsReminderEnabled,
    skipWalkins,
    allowReimport,
    allowRebalancing,
    assignToExistingSlots,
    existingTicketTypeId,
    guestFullNameOrder,
    resoFullNameOrder,
    multipleHeaderDetection,
    markForDeletion,
    disableIndexing,
  } = createImporterConfig(input);
  const queryParams = stringify({
    fileType,
    fileName,
    formatVersion,
    separator,
    existingTicketTypeId,
    guestFullNameOrder,
    resoFullNameOrder,
  });
  return {
    TockApi: {
      postFile: `/data/import/${input.businessId}?${queryParams}
      &smsReminderEnabled=${smsReminderEnabled}
      &skipWalkins=${skipWalkins}
      &allowReimport=${allowReimport}
      &allowRebalancing=${allowRebalancing}
      &assignToExistingSlots=${assignToExistingSlots}
      &multipleHeaderDetection=${multipleHeaderDetection}
      &markForDeletion=${markForDeletion}
      &disableIndexing=${disableIndexing}
      &async=true`,
      type: ClowncarAppConstants.IMPORT_UPLOAD,
      request: file,
      scope: {
        businessId: toNumber(input.businessId),
      },
    },
  };
}
